import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import api from "../lib/api";
import { useAuth } from "./AuthContext";

const THEME_KEY = "iris_theme";
const ThemeContext = createContext(null);

export const useTheme = () => useContext(ThemeContext);

// Allowed persisted values. `auto` means "follow the OS `prefers-color-scheme`".
const VALID = new Set(["light", "dark", "auto"]);

function readSystemTheme() {
  if (typeof window === "undefined" || !window.matchMedia) return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

/** Resolve `auto` to the concrete light/dark value the OS currently reports. */
function resolveTheme(pref) {
  if (pref === "auto") return readSystemTheme();
  return pref === "dark" ? "dark" : "light";
}

/**
 * ThemeProvider — reads/writes `data-theme="dark|light"` on <html>.
 *
 * User preference (`theme`) can be `light`, `dark`, or `auto`. When `auto`,
 * we listen to the OS `prefers-color-scheme` media query and paint the
 * resolved value; the preference itself is still stored as `auto` so the
 * app keeps following the OS on subsequent toggles.
 *
 * Persistence order for FIRST paint (before auth resolves):
 *   localStorage → "light" (light is the product default).
 *
 * Once the user is authenticated we hydrate from `GET /users/me/preferences.theme`
 * (server is source of truth so the theme follows the user across devices), and
 * every subsequent change is PATCHed back (debounced 400ms).
 */
export function ThemeProvider({ children }) {
  // `theme` = the user's preference ("light" | "dark" | "auto").
  const [theme, setThemeState] = useState(() => {
    if (typeof window === "undefined") return "light";
    try {
      const stored = localStorage.getItem(THEME_KEY);
      if (VALID.has(stored)) return stored;
    } catch (err) { /* ignore */ }
    return "light";
  });

  // `resolved` = what actually paints on <html> (never "auto").
  const [resolved, setResolved] = useState(() => resolveTheme(theme));

  // Recompute resolved whenever the preference or the OS scheme changes.
  useEffect(() => {
    setResolved(resolveTheme(theme));
    if (theme !== "auto" || typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => setResolved(readSystemTheme());
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, [theme]);

  // Paint + cache immediately on every change (works pre-auth too)
  useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.setAttribute("data-theme", resolved);
    try { localStorage.setItem(THEME_KEY, theme); }
    catch (err) { if (process.env.NODE_ENV !== "production") console.warn("theme persist failed:", err); }
  }, [theme, resolved]);

  // Auth-aware server sync
  const authCtx = useAuth?.() || {};
  const user = authCtx.user;
  const hydratedRef = useRef(false);

  const hydrateFromServer = useCallback(() => {
    api.get("/users/me/preferences").then((res) => {
      const t = res?.data?.theme;
      if (VALID.has(t)) setThemeState(t);
    }).catch(() => { /* offline / 401 — keep localStorage value */ });
  }, []);

  // Hydrate once per session on login. Also re-hydrate when a `copy-from`
  // event fires so a pinned layout can carry theme too.
  useEffect(() => {
    if (!user) { hydratedRef.current = false; return undefined; }
    if (hydratedRef.current) return undefined;
    hydratedRef.current = true;
    hydrateFromServer();
    const onUpdate = () => hydrateFromServer();
    window.addEventListener("irisguard:prefs-updated", onUpdate);
    return () => window.removeEventListener("irisguard:prefs-updated", onUpdate);
  }, [user, hydrateFromServer]);

  // Debounced PATCH back to server after any change (post-hydration only)
  useEffect(() => {
    if (!user || !hydratedRef.current) return undefined;
    const handle = setTimeout(() => {
      api.patch("/users/me/preferences", { theme }).catch(() => { /* best-effort */ });
    }, 400);
    return () => clearTimeout(handle);
  }, [user, theme]);

  const setTheme = useCallback((t) => setThemeState(VALID.has(t) ? t : "light"), []);
  // Cycle Light → Dark → Auto → Light. Kept as `toggle` for backwards
  // compatibility with existing callers.
  const toggle = useCallback(() => setThemeState((t) => (
    t === "light" ? "dark" : t === "dark" ? "auto" : "light"
  )), []);

  return (
    <ThemeContext.Provider value={{ theme, resolved, setTheme, toggle }}>
      {children}
    </ThemeContext.Provider>
  );
}
